import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HiOutlineTrash } from "react-icons/hi2";
import { LuGripVertical, LuPalette, LuPlus } from "react-icons/lu";
import { categoryIconsMap } from "../../config/categoryIcons";
import { entityKey } from "../../hooks/useMenuEditor";
import SortableMenuItem from "./SortableMenuItem";

const SortableCategoryCard = ({ category, filteredItems, onRename, onAppearance, onAddItem, onEditItem, onDuplicateItem, onDeleteItem, onDeleteCategory, onMoveItem }) => {
  const id = entityKey(category);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const Icon = categoryIconsMap[category.icon];

  const itemDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) onMoveItem(String(active.id), String(over.id));
  };

  return (
    <section
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded-2xl border border-gray-100 bg-white p-4 sm:p-5 ${isDragging ? "z-20 opacity-70 shadow-md" : "shadow-sm"}`}
    >
      <header className="mb-4 flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center">
        <button {...attributes} {...listeners} type="button" aria-label={`جابجایی دسته ${category.name}`} className="inline-flex cursor-grab touch-none rounded-lg p-2 text-gray-400 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30"><LuGripVertical /></button>
        {Icon && <Icon aria-hidden="true" style={{ color: category.iconColor }} className="text-xl" />}
        <input
          value={category.name}
          onChange={(event) => onRename(event.target.value)}
          aria-label="نام دسته‌بندی"
          className="min-w-0 flex-1 rounded-lg border border-transparent px-2 py-1.5 text-lg font-bold text-gray-800 outline-none hover:border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
        <div className="flex flex-wrap items-center justify-end gap-1">
          <button type="button" onClick={onAddItem} className="inline-flex min-h-10 items-center gap-1.5 rounded-xl bg-primary/10 px-3 text-sm font-bold text-primary hover:bg-primary/15"><LuPlus /> افزودن آیتم</button>
          <button type="button" onClick={onAppearance} aria-label={`تغییر ظاهر ${category.name}`} title="ظاهر دسته" className="rounded-lg p-2.5 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"><LuPalette /></button>
          <button type="button" onClick={onDeleteCategory} aria-label={`حذف دسته ${category.name}`} title="حذف دسته" className="rounded-lg p-2.5 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"><HiOutlineTrash /></button>
        </div>
      </header>
      {filteredItems.length ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={itemDragEnd}>
          <SortableContext items={filteredItems.map(entityKey)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {filteredItems.map((item) => (
                <SortableMenuItem
                  key={entityKey(item)}
                  item={item}
                  onEdit={() => onEditItem(item)}
                  onDuplicate={() => onDuplicateItem(entityKey(item))}
                  onDelete={() => onDeleteItem(item)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">{category.items.length ? "آیتمی مطابق جست‌وجو پیدا نشد." : "هنوز آیتمی در این دسته نیست."}</div>
      )}
    </section>
  );
};

export default SortableCategoryCard;
